/**
 * Phase 20 Plan 20-02 — SubscriptionMatrixTab
 *
 * 8-row × N-recipient checkbox matrix per D-189.
 * Each recipient column has its own dirty state + Save/Reset buttons.
 *
 * Architecture: ColumnManager components own per-column state via hooks;
 * they register toggle/save/reset callbacks upward via onStateChange so the
 * parent table can render cells that invoke them.
 */

import type { JSX } from 'react';
import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useNotificationTypesQuery,
  useRecipientsQuery,
  useRecipientDetailQuery,
  useUpdateSubscriptionsMutation,
} from '@/hooks/useNotificationsQueries';
import { toast } from '@/lib/toast';
import type { NotificationTypeInfoDto, NotificationTypeValue, RecipientListItemDto } from '@/types/notifications';

// ---------------------------------------------------------------------------
// Per-column state record (held by parent)
// ---------------------------------------------------------------------------

interface ColMeta {
  readonly enabled: Readonly<Record<string, boolean>>;
  readonly dirty: boolean;
  readonly isPending: boolean;
  readonly toggle: (typeName: string) => void;
  readonly save: () => void;
  readonly reset: () => void;
}

// ---------------------------------------------------------------------------
// ColumnManager — invisible component that owns one column's hook state
// ---------------------------------------------------------------------------

interface ColumnManagerProps {
  readonly recipient: RecipientListItemDto;
  readonly notificationTypes: NotificationTypeInfoDto[];
  readonly onStateChange: (recipientId: number, meta: ColMeta) => void;
}

function ColumnManager({ recipient, notificationTypes, onStateChange }: ColumnManagerProps): null {
  const { data: detail } = useRecipientDetailQuery(recipient.id);
  const updateSubs = useUpdateSubscriptionsMutation();

  const [localEnabled, setLocalEnabled] = useState<Readonly<Record<string, boolean>>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (detail === undefined) return;
    const map: Record<string, boolean> = {};
    for (const sub of detail.subscriptions) {
      map[sub.notificationType] = sub.isEnabled;
    }
    setLocalEnabled(map);
    setDirty(false);
  }, [detail]);

  const toggle = (typeName: string): void => {
    setLocalEnabled((prev) => ({ ...prev, [typeName]: !(prev[typeName] ?? false) }));
    setDirty(true);
  };

  const save = (): void => {
    const items = notificationTypes.map((t) => ({
      notificationType: t.name as NotificationTypeValue,
      isEnabled: localEnabled[t.name] ?? false,
    }));
    void updateSubs
      .mutateAsync({ recipientId: recipient.id, body: { items } })
      .then(() => {
        toast.success(`Subscriptions saved for ${recipient.displayName}`);
        setDirty(false);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Save failed';
        toast.error(msg);
      });
  };

  const reset = (): void => {
    if (detail === undefined) return;
    const map: Record<string, boolean> = {};
    for (const sub of detail.subscriptions) {
      map[sub.notificationType] = sub.isEnabled;
    }
    setLocalEnabled(map);
    setDirty(false);
  };

  // Propagate current state upward every render
  useEffect(() => {
    onStateChange(recipient.id, {
      enabled: localEnabled,
      dirty,
      isPending: updateSubs.isPending,
      toggle,
      save,
      reset,
    });
  });

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SubscriptionMatrixTab(): JSX.Element {
  const { data: typesData } = useNotificationTypesQuery();
  const { data: recipientsData } = useRecipientsQuery({ pageSize: 50 });

  const notificationTypes = typesData ?? [];
  const recipients = recipientsData?.items.filter((r) => r.isActive) ?? [];

  const [colMeta, setColMeta] = useState<Readonly<Record<number, ColMeta>>>({});

  const handleStateChange = (recipientId: number, meta: ColMeta): void => {
    setColMeta((prev) => {
      const existing = prev[recipientId];
      // Avoid infinite loop: only update if enabled map, dirty, or isPending changed
      if (existing === undefined) {
        return { ...prev, [recipientId]: meta };
      }
      if (
        existing.dirty === meta.dirty &&
        existing.isPending === meta.isPending &&
        existing.enabled === meta.enabled
      ) {
        return prev;
      }
      return { ...prev, [recipientId]: meta };
    });
  };

  return (
    <div data-testid="subscription-matrix-tab" className="overflow-auto">
      {/* Invisible column managers — own hook state per recipient */}
      {recipients.map((r) => (
        <ColumnManager
          key={r.id}
          recipient={r}
          notificationTypes={notificationTypes}
          onStateChange={handleStateChange}
        />
      ))}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-56">Notification Type</TableHead>
            {recipients.map((r) => (
              <TableHead
                key={r.id}
                className="text-center"
                data-testid={`matrix-col-header-${r.id}`}
              >
                {r.displayName}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {notificationTypes.map((t) => (
            <TableRow key={t.name} data-testid={`matrix-row-${t.name}`}>
              <TableCell>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.description}</div>
              </TableCell>
              {recipients.map((r) => {
                const meta = colMeta[r.id];
                return (
                  <TableCell key={r.id} className="text-center">
                    <Checkbox
                      data-testid={`matrix-cell-${r.id}-${t.name}`}
                      checked={meta?.enabled[t.name] ?? false}
                      onCheckedChange={() => {
                        meta?.toggle(t.name);
                      }}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}

          {/* Save/Reset row */}
          {recipients.length > 0 && (
            <TableRow>
              <TableCell className="text-sm text-muted-foreground">Actions</TableCell>
              {recipients.map((r) => {
                const meta = colMeta[r.id];
                return (
                  <TableCell key={r.id} className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button
                        size="sm"
                        disabled={!(meta?.dirty ?? false) || (meta?.isPending ?? false)}
                        onClick={() => { meta?.save(); }}
                        data-testid={`matrix-save-btn-${r.id}`}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!(meta?.dirty ?? false)}
                        onClick={() => { meta?.reset(); }}
                        data-testid={`matrix-reset-btn-${r.id}`}
                      >
                        Reset
                      </Button>
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
